// The Translator — who sees what language, for the whole app (no React context
// to thread through; the same external-store pattern as lookAndFeel.js).
//
//   applyTranslator(cfg)          the site's setting (GET /api/pwa-global-settings → translator),
//                                 applied at sign-in and the moment an admin saves it
//   setTranslatorAccount(id)      whose choice to read (each person keeps their own, per browser,
//                                 like the theme)
//   chooseLanguage(code)          the dictionary icon: 'en' | 'af' | 'de' | 'pl'
//   useTranslator()               { enabled, languages, choice, active, loading } for the shell
//   translatorMisses()            what was on screen with no translation (Settings → Translator)
//
// OFF unless the site's setting says enabled: true — then nothing loads: the
// engine and the dictionaries are their own chunks, fetched only when a person
// picks a language other than English.
import { useSyncExternalStore } from 'react';
import * as clientConfig from '@client/config/client.config';
import { normalizeSetting, compile, CODES, wordsOf } from './core.js';
import { createSwitch } from './switcher.js';

// A client fork may add its own words (its custom pages, its site's names) through the client
// config seam, one entry per language — the words, or (better) a loader, so a site with the
// Translator off downloads nothing:
//   export const TRANSLATIONS = { af: () => import('./translations/af.json'), de: …, pl: … };
// Read through the namespace (once): a fork whose client.config.js has no such export keeps
// building (a named import of a missing export fails the build) — as workspacesEnabled.js does.
// The name goes in as a key, so the build does not warn on every fork that has no such export.
const SEAM = 'TRANSLATIONS';
const CLIENT_RAW = clientConfig[SEAM];
const CLIENT = CLIENT_RAW && typeof CLIENT_RAW === 'object' ? CLIENT_RAW : {};
const clients = {};
const loadClient = (lang) => {
  if (!clients[lang]) {
    const v = CLIENT[lang];
    clients[lang] = Promise.resolve(typeof v === 'function' ? v() : v)
      .then(wordsOf)
      .catch(() => ({}));
  }
  return clients[lang];
};
const PREFIX = clientConfig.STORAGE_PREFIX || 'stride';

const choiceKey = (acc) => `${PREFIX}_lang${acc ? `_${acc}` : ''}`;
const SITE_KEY = `${PREFIX}_translator_site`;
const read = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const write = (k, v) => { try { if (v == null) localStorage.removeItem(k); else localStorage.setItem(k, v); } catch { /* private window */ } };

// the last setting this browser saw (for 12 hours): the page can start in the chosen language
// before the site's setting arrives (no English flash on every load); the setting itself has the
// last word — and a switch-off reaches a browser at the latest 12 hours later even if it never
// reads the setting again
const CACHE_MS = 12 * 3600 * 1000;
let setting = (() => {
  try {
    const c = JSON.parse(read(SITE_KEY) || 'null');
    return c && typeof c.at === 'number' && Date.now() - c.at < CACHE_MS ? normalizeSetting(c) : normalizeSetting(null);
  } catch { return normalizeSetting(null); }
})();
let account = null;
let choice = CODES.includes(read(choiceKey(null))) ? read(choiceKey(null)) : 'en';
let active = 'en';
let loading = false;
let engine = null;
const subs = new Set();
let snap = null;
const makeSnap = () => ({ enabled: setting.enabled, languages: setting.languages, choice, active, loading });
const emit = () => { snap = makeSnap(); subs.forEach((fn) => fn()); };

const products = {};
const loadProduct = (lang) => {
  if (!products[lang]) {
    products[lang] = (lang === 'af' ? import('./dictionaries/af.json')
      : lang === 'de' ? import('./dictionaries/de.json')
        : import('./dictionaries/pl.json')).then(wordsOf).catch(() => ({}));
  }
  return products[lang];
};
const glossaryLayer = (lang) => {
  const out = {};
  for (const [en, row] of Object.entries(setting.glossary || {})) if (row && row[lang]) out[en] = row[lang];
  return out;
};

// what should be on screen: the person's choice when the site has the Translator on and offers it
const wanted = () => (setting.enabled && choice !== 'en' && setting.languages.includes(choice) ? choice : 'en');

// the switching itself (./switcher.js): the last pick wins, whatever loads first
const sw = createSwitch({
  async load(lang) {
    const [{ createEngine }, product, client] = await Promise.all([import('./engine.js'), loadProduct(lang), loadClient(lang)]);
    if (!engine) engine = createEngine(typeof document !== 'undefined' ? document : null);
    return compile([glossaryLayer(lang), client, product]);
  },
  show(lang, compiled) { engine.use(lang, compiled); },
  english() { if (engine) engine.stop(); },
  onChange() { active = sw.state.shown; loading = sw.state.loading; emit(); },
});
function sync() {
  const want = wanted();
  sw.set(want, want === 'en' ? 'en' : `${want}:${JSON.stringify(setting.glossary)}`)
    // the page stays English — a missing chunk must never break a screen
    .catch((e) => { if (typeof console !== 'undefined') console.warn('[translator]', e && e.message); });
}

export function applyTranslator(cfg) {
  setting = normalizeSetting(cfg);
  write(SITE_KEY, setting.enabled ? JSON.stringify({ enabled: true, languages: setting.languages, glossary: setting.glossary, at: Date.now() }) : null);
  emit();
  sync();
}

export function setTranslatorAccount(accountId) {
  account = accountId || null;
  const mine = read(choiceKey(account));
  choice = CODES.includes(mine) ? mine : 'en';
  emit();
  sync();
}

export function chooseLanguage(code) {
  choice = code === 'en' || CODES.includes(code) ? code : 'en';
  write(choiceKey(account), choice);
  write(choiceKey(null), choice); // the browser's last choice, for the first paint
  emit();
  sync();
}

const subscribe = (fn) => { subs.add(fn); return () => subs.delete(fn); };
const getSnap = () => { if (!snap) snap = makeSnap(); return snap; };
export function useTranslator() { return useSyncExternalStore(subscribe, getSnap, getSnap); }
export const translatorMisses = () => (engine ? engine.misses() : []);
export const translatorClientWords = (lang) => loadClient(lang);
export const translatorProductWords = (lang) => loadProduct(lang);
// the built-in words of a language (this site's client words over the product's), compiled the
// way the engine reads them — the Settings card shows what a site term already reads as
export const translatorBuiltIn = async (lang) => { const [p, c] = await Promise.all([loadProduct(lang), loadClient(lang)]); return compile([c, p]); };

// the first paint: this browser's last setting and choice
if (typeof window !== 'undefined' && wanted() !== 'en') sync();
